import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Chartjs from "chart.js";
import { DivCharts, Item, Contenedor, Contenido, Titulo, Seccion, SeccionTitulo, GridTotales, GridGraficos, GridFinanzasCards } from "./styles";
import { fetchStats } from "../../services/firebase/stsMensual/index";
import { fetchResumenMensajes } from "../../services/firebase/resumenMensajes/index";
import Spinner from "react-bootstrap/Spinner";

const themes = {
  blue: {
    bg: "rgba(12, 141, 227, 0.2)",
    border: "rgba(12, 141, 227, 1)"
  },
  navy: {
    bg: "rgba(12, 35, 76, 0.2)",
    border: "rgba(12, 35, 76, 1)"
  },
  orange: {
    bg: "rgba(255, 159, 64, 0.2)",
    border: "rgba(255, 159, 64, 1)"
  },
  teal: {
    bg: "rgba(103, 210, 220, 0.2)",
    border: "rgba(103, 210, 220, 1)"
  },
  purple: {
    bg: "rgba(153, 102, 255, 0.2)",
    border: "rgba(153, 102, 255, 1)"
  }
};

const getConfig = (data, titulo, themeKey = "blue") => {
  const theme = themes[themeKey] || themes.blue;
  return {
    type: "bar",
    data: {
      labels: [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
      ],
      datasets: [
        {
          label: titulo,
          data: data,
          backgroundColor: Array(12).fill(theme.bg),
          borderColor: Array(12).fill(theme.border),
          borderWidth: 1.5,
        },
      ],
    },
    options: {
      legend: {
        display: false,
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    },
  };
};

const Chart = ({ data, titulo, themeKey }) => {
  const chartContainer = useRef(null);

  useEffect(() => {
    let newChartInstance;
    if (chartContainer && chartContainer.current) {
      newChartInstance = new Chartjs(
        chartContainer.current,
        getConfig(data, titulo, themeKey)
      );
    }
    return () => {
      if (newChartInstance) {
        newChartInstance.destroy();
      }
    };
  }, [chartContainer, data, titulo, themeKey]);

  return (
    <div id="holaaCanva">
      <canvas ref={chartContainer} />
    </div>
  );
};

export const Dashboard = () => {
  const sesion = useSelector((state) => state.sesion);
  const [estado, setEstado] = React.useState({
    loading: false,
    data: [],
    error: "",
  });
  const [msgEstado, setMsgEstado] = React.useState({
    loading: false,
    data: Array(12).fill(0),
    currentMonthCount: 0,
  });
  let fecha = new Date();
  // Se setea la fecha a un día anterior, debido a que el proceso de cierre corre al inicio del día siguiente
  fecha.setDate(fecha.getDate() - 1);
  let month = fecha.getMonth();

  const traerEstadisticas = async (empresaId) => {
    setEstado((prev) => ({ ...prev, loading: true }));
    setMsgEstado((prev) => ({ ...prev, loading: true }));
    try {
      const [statsData, msgStats] = await Promise.all([
        fetchStats(empresaId, fecha.getFullYear()),
        fetchResumenMensajes(empresaId, fecha.getFullYear())
      ]);

      statsData.sort(function (a, b) {
        if (a.month > b.month) {
          return 1;
        }
        if (a.month < b.month) {
          return -1;
        }
        // a must be equal to b
        return 0;
      });

      const monthlyCounts = Array(12).fill(0);
      msgStats.forEach((item) => {
        if (item.fecha) {
          const parts = item.fecha.split("-");
          if (parts.length >= 2) {
            const m = parseInt(parts[1], 10) - 1;
            const outbound = item.outboundCount || 0;
            if (m >= 0 && m < 12) {
              monthlyCounts[m] += outbound;
            }
          }
        }
      });

      setEstado({ loading: false, data: statsData, error: "" });
      setMsgEstado({
        loading: false,
        data: monthlyCounts,
        currentMonthCount: monthlyCounts[month] || 0,
      });
    } catch (err) {
      console.error(err);
      setEstado({ loading: false, data: [], error: err.message });
      setMsgEstado({ loading: false, data: Array(12).fill(0), currentMonthCount: 0 });
    }
  };

  React.useEffect(() => {
    traerEstadisticas(sesion.usuario.empresaId);
  }, []);

  const getSts = (month) => {
    const idx = estado.data.findIndex((item) => item.month === month);
    if (idx > -1) {
      return estado.data[idx];
    }
    return {
      "#abonos": undefined,
      "#pacientes": undefined,
      "#prestaciones": undefined,
      abonos: undefined,
      compras: undefined,
      deuda: undefined,
      empresaId: undefined,
      gastosFijos: undefined,
      gastosVariables: undefined,
      year: undefined,
      month: undefined,
    };
  };

  if (estado.loading || msgEstado.loading)
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden"></span>
      </Spinner>
    );

  return (
    <Contenedor>
      {/* Capa 1: 💰 Finanzas (real) */}
      <Seccion>
        <SeccionTitulo>💰 Finanzas (real)</SeccionTitulo>
        
        {/* Tarjetas resumen */}
        <GridFinanzasCards>
          <Item>
            <Titulo>Ingresos (Abonos)</Titulo>
            <Contenido>{getSts(month).abonos || 0}</Contenido>
          </Item>
          <Item>
            <Titulo>Gastos Fijos</Titulo>
            <Contenido>{getSts(month).gastosFijos || 0}</Contenido>
          </Item>
          <Item>
            <Titulo>Gastos Variables</Titulo>
            <Contenido>{getSts(month).gastosVariables || 0}</Contenido>
          </Item>
          <Item>
            <Titulo>Utilidad</Titulo>
            <Contenido>
              {Math.round(((getSts(month).abonos || 0) -
                (getSts(month).gastosFijos || 0) -
                (getSts(month).gastosVariables || 0)) * 100) / 100}
            </Contenido>
          </Item>
          <Item>
            <Titulo>Por Cobrar (Deuda)</Titulo>
            <Contenido>{getSts(month).deuda || 0}</Contenido>
          </Item>
        </GridFinanzasCards>

        {/* Gráficos históricos de finanzas */}
        <GridGraficos>
          <Item>
            <Titulo>Historial de Ingresos</Titulo>
            <Chart
              titulo="Ingresos"
              themeKey="blue"
              data={estado.data.map((item) => item.abonos || 0)}
            />
          </Item>

          <Item>
            <Titulo>Historial de Gastos Variables</Titulo>
            <Chart
              titulo="Gastos Variables"
              themeKey="orange"
              data={estado.data.map((item) => item.gastosVariables || 0)}
            />
          </Item>
          
          <Item>
            <Titulo>Historial de Utilidad</Titulo>
            <Chart
              titulo="Utilidad"
              themeKey="teal"
              data={estado.data.map(
                (item) => Math.round(((item.abonos || 0) - (item.gastosFijos || 0) - (item.gastosVariables || 0)) * 100) / 100
              )}
            />
          </Item>
        </GridGraficos>
      </Seccion>

      {/* Capa 2: 🦷 Producción (operativa) */}
      <Seccion>
        <SeccionTitulo>🦷 Producción (operativa)</SeccionTitulo>
        <GridGraficos>
          <Item>
            <Titulo>Pacientes Nuevos ({getSts(month)["#pacientes"] || 0})</Titulo>
            <Chart
              titulo="Pacientes"
              themeKey="blue"
              data={estado.data.map((item) => item["#pacientes"] || 0)}
            />
          </Item>
          <Item>
            <Titulo>Prestaciones Realizadas ({getSts(month)["#prestaciones"] || 0})</Titulo>
            <Chart
              titulo="Prestaciones"
              themeKey="purple"
              data={estado.data.map((item) => item["#prestaciones"] || 0)}
            />
          </Item>
        </GridGraficos>
      </Seccion>

      {/* Capa 3: 📈 Marketing */}
      <Seccion>
        <SeccionTitulo>📈 Marketing</SeccionTitulo>
        <GridGraficos>
          <Item>
            <Titulo>Mensajes Enviados ({msgEstado.currentMonthCount || 0})</Titulo>
            <Chart
              titulo="Mensajes Enviados"
              themeKey="navy"
              data={msgEstado.data}
            />
            <div style={{ marginTop: '15px', textAlign: 'center' }}>
              <Link to="/admin/reportes/whatsapp" className="btn btn-primary btn-sm fw-bold" style={{ backgroundColor: '#0c8de3', borderColor: '#0c8de3' }}>
                Ver Reporte Detallado →
              </Link>
            </div>
          </Item>
        </GridGraficos>
      </Seccion>
    </Contenedor>
  );
};


